import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { Score, ScoreData, ScoreMatchPlay } from "../models/scoreModel";
import { scoresService } from "../services/scoresService";
import useAuthStore from "./authStore";
import { toast } from "react-toastify";
import { storeVersion, migrate, onRehydrateStorage } from "./versioning";

const defaultValues = {
  version: storeVersion,
  isBusy: false,
  busyMessage: "",
  allDetailsForScoreEntry: null,
};

interface ScoresStore {
  version: number;
  isBusy: boolean;
  busyMessage: string;
  allDetailsForScoreEntry: ScoreData | null;
  addScore: (score: Score) => void;
  updateScore: (score: Score) => void;
  uploadScoresByPlayer: () => void;
  getScoresToStartEntry: (playerId: string) => void;

  addMatchPlayScore: (score: ScoreMatchPlay) => void;
  updateMatchPlayScore: (score: ScoreMatchPlay) => void;
  uploadMatchPlayScoresByPlayer: () => void;
}

export const useScoresStore = create<ScoresStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...defaultValues,
        addScore: async (score: Score) => {
          try {
            let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
            if (allDetailsForScoreEntry) {
              const scores = allDetailsForScoreEntry.scores;

              allDetailsForScoreEntry.scores = [...scores, score];
              set({ allDetailsForScoreEntry });
            }

            set({ isBusy: true, busyMessage: "Adding score..." });
            const res = await scoresService.addScore(score);
            if (res.statusCode === 200) {
              let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
              if (allDetailsForScoreEntry) {
                allDetailsForScoreEntry.scores =
                  allDetailsForScoreEntry.scores.map((s: { id: any }) =>
                    s.id === res.result.id ? res.result : s
                  );
                set({ allDetailsForScoreEntry });
              }
            }
            set({ isBusy: false });
          } catch (error) {
            // toast.error("Failed to add score");
          } finally {
            set({ isBusy: false });
          }
        },
        updateScore: async (score: Score) => {
          let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
          if (allDetailsForScoreEntry) {
            const existingScore = allDetailsForScoreEntry.scores.find(
              (s: { id: string }) => s.id === score.id
            );
            if (existingScore) {
              allDetailsForScoreEntry.scores =
                allDetailsForScoreEntry.scores.map((s: Score) =>
                  s.id === score.id ? score : s
                );
            }
            set({ allDetailsForScoreEntry });
          }
          try {
            set({ isBusy: true, busyMessage: "Updating score..." });
            const res = await scoresService.updateScore(score);
            if (res.statusCode === 200) {
              let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
              if (allDetailsForScoreEntry) {
                allDetailsForScoreEntry.scores =
                  allDetailsForScoreEntry.scores.map((s: { id: any }) =>
                    s.id === res.result.id ? res.result : s
                  );
                set({ allDetailsForScoreEntry });
              }
            }
            // toast.success("Score updated successfully");
            set({ isBusy: false });
          } catch (error) {
            // toast.error("Failed to update score");
            set({ isBusy: false });
          } finally {
            set({ isBusy: false });
          }
        },
        uploadScoresByPlayer: async () => {
          try {
            set({ isBusy: true, busyMessage: "Uploading scores..." });
            const allDetailsForScoreEntry = get().allDetailsForScoreEntry;
            if (!allDetailsForScoreEntry || !allDetailsForScoreEntry.scores) {
              return;
            }
            const scores = allDetailsForScoreEntry.scores;
            const scoresToUpload = scores.filter(
              (score: { isUploaded: any }) => !score.isUploaded
            );
            if (scoresToUpload && scoresToUpload.length > 0) {
              toast.info("Uploading scores...");
              const res = await scoresService.uploadScores(scoresToUpload);
              if (res.statusCode === 200) {
                const currentUser = useAuthStore.getState().currentUser;
                if (currentUser && currentUser.playerId) {
                  get().getScoresToStartEntry(currentUser.playerId);
                } else {
                  get().getScoresToStartEntry(currentUser?.id);
                }
              }
            }
            // toast.success("Scores uploaded successfully");
          } catch (error) {
            // toast.error("Failed to upload scores");
          } finally {
            set({ isBusy: false });
          }
        },
        addMatchPlayScore: async (score: ScoreMatchPlay) => {
          try {
            let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
            if (allDetailsForScoreEntry) {
              const scores = allDetailsForScoreEntry.scores;

              allDetailsForScoreEntry.scores = [...scores, score];
              set({ allDetailsForScoreEntry });
            }

            set({ isBusy: true, busyMessage: "Adding score..." });
            const res = await scoresService.addMatchPlayScore(score);
            if (res.statusCode === 200) {
              let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
              if (allDetailsForScoreEntry) {
                allDetailsForScoreEntry.scores =
                  allDetailsForScoreEntry.scores.map((s: { id: any }) =>
                    s.id === res.result.id ? res.result : s
                  );
                set({ allDetailsForScoreEntry });
              }
            }
            set({ isBusy: false });
          } catch (error) {
            // toast.error("Failed to add score");
          } finally {
            set({ isBusy: false });
          }
        },
        updateMatchPlayScore: async (score: ScoreMatchPlay) => {
          let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
          if (allDetailsForScoreEntry) {
            const existingScore = allDetailsForScoreEntry.scores.find(
              (s: { id: string }) => s.id === score.id
            );
            if (existingScore) {
              allDetailsForScoreEntry.scores =
                allDetailsForScoreEntry.scores.map((s: Score) =>
                  s.id === score.id ? score : s
                );
            }
            set({ allDetailsForScoreEntry });
          }
          try {
            set({ isBusy: true, busyMessage: "Updating score..." });
            const res = await scoresService.updateMatchPlayScore(score);
            if (res.statusCode === 200) {
              let allDetailsForScoreEntry = get().allDetailsForScoreEntry;
              if (allDetailsForScoreEntry) {
                allDetailsForScoreEntry.scores =
                  allDetailsForScoreEntry.scores.map((s: { id: any }) =>
                    s.id === res.result.id ? res.result : s
                  );
                set({ allDetailsForScoreEntry });
              }
            }
            // toast.success("Score updated successfully");
            set({ isBusy: false });
          } catch (error) {
            // toast.error("Failed to update score");
            set({ isBusy: false });
          } finally {
            set({ isBusy: false });
          }
        },
        uploadMatchPlayScoresByPlayer: async () => {
          try {
            set({ isBusy: true, busyMessage: "Uploading scores..." });
            const allDetailsForScoreEntry = get().allDetailsForScoreEntry;
            if (!allDetailsForScoreEntry || !allDetailsForScoreEntry.scores) {
              return;
            }
            const scores = allDetailsForScoreEntry.scores;
            const scoresToUpload = scores.filter(
              (score: { isUploaded: any }) => !score.isUploaded
            );
            if (scoresToUpload && scoresToUpload.length > 0) {
              toast.info("Uploading scores...");
              const res = await scoresService.uploadMatchPlayScores(
                scoresToUpload
              );
              if (res.statusCode === 200) {
                const currentUser = useAuthStore.getState().currentUser;
                if (currentUser && currentUser.playerId) {
                  get().getScoresToStartEntry(currentUser.playerId);
                } else {
                  get().getScoresToStartEntry(currentUser?.id);
                }
              }
            }
            // toast.success("Scores uploaded successfully");
          } catch (error) {
            // toast.error("Failed to upload scores");
          } finally {
            set({ isBusy: false });
          }
        },
        getScoresToStartEntry: async (playerId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Loading scores..." });
            const currentUser = useAuthStore.getState().currentUser;
            if (
              currentUser &&
              ["admin", "dataEntry"].includes(currentUser.role)
            ) {
              const scores = await scoresService.getScoresToStartEntryAdmin();
              if (scores.statusCode === 200) {
                set({ allDetailsForScoreEntry: scores.result });
              } else if (scores.statusCode === 404) {
                set({ allDetailsForScoreEntry: null });
              }
              return;
            } else if (playerId) {
              const scores = await scoresService.getScoresToStartEntry(
                playerId
              );
              if (scores.statusCode === 200) {
                set({ allDetailsForScoreEntry: scores.result });
              } else if (scores.statusCode === 404) {
                set({ allDetailsForScoreEntry: null });
              }
            } else {
              toast.error("Failed to load scores");
            }
          } catch (error) {
            // toast.error("Failed to load scores");
            set({ isBusy: false });
          } finally {
            set({ isBusy: false });
          }
        },
      }),
      {
        name: "ScoresStore",
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: onRehydrateStorage(defaultValues),
        migrate: migrate(defaultValues),
      }
    ),
    { name: "ScoresStore", enabled: process.env.NODE_ENV === "development" }
  )
);
